//july 25/FormLayout.jsx without scrollable sidepanel fixes
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, CircularProgress, Snackbar, Alert } from '@mui/material';
import SidePanel from '../components/FormSettings/SidePanel';
import TextFieldPreview from '../components/Fields/TextFieldPreview';
import URLFieldPreview from '../components/Fields/URLFieldPreview';
import SingleSelectFieldPreview from '../components/Fields/SingleSelectFieldPreview'; // Updated import
import DatePickerFieldPreview from '../components/Fields/DatePickerFieldPreview';
import MultiSelectFieldPreview from '../components/Fields/MultiSelectFieldPreview';
import AttachFilePreview from '../components/Fields/AttachFilePreview';
import MultiLineTextPreview from '../components/Fields/MultiLineTextPreview';
import HeaderPreview from '../components/Fields/HeaderPreview';
import ImagePreview from '../components/Fields/ImagePreview';
import OrganizationManagedFieldPreview from '../components/Fields/OrganizationManagedFieldPreview';
import FormLayoutUI from '../components/FormSettings/FormDesignLayout/FormLayoutUI';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useFormContext } from '../contexts/FormContext'; // Using form context for formId
import { useFormLayout } from '../contexts/FormLayoutContext'; // Using FormLayoutContext for layout management
import { useFieldEdit } from '../contexts/FieldEditContext'; // Ensuring FieldEditContext is used for maintaining field edits
import { useFieldMetadata } from '../contexts/FieldMetadataContext'; // Correctly import useFieldMetadata
import { useAuth } from '../contexts/AuthContext';
//AssetUploadFormSpecific
import AssetTypeFieldPreview from '../components/Fields/AssetTypeFieldPreview';
import VersionNumberFieldPreview from '../components/Fields/VersionNumberFieldPreview';
import BuilderDragLayer from '../components/FormSettings/FormDesignLayout/BuilderDragLayer';

import { listManagedFieldsForOrganization } from '../utils/managedFields';

const BASE_LAYOUT_SECTIONS = [
  {
    title: 'New fields',
    items: [
      { element: <TextFieldPreview id="textField1" index={0} /> },
      { element: <URLFieldPreview id="urlField1" index={1} /> },
      {
        element: (
          <SingleSelectFieldPreview
            id="singleSelectField1"
            index={2}
          />
        ),
      },
       {
         element: (
           <MultiSelectFieldPreview
             id="multiSelectField1"
             index={4}
           />
         ),
       },
      {
        element: (
          <DatePickerFieldPreview
            id="datePickerField1"
            index={3}
          />
        ),
      },
      { element: <AttachFilePreview id="attachFile1" index={5} /> },
      {
        element: (
          <MultiLineTextPreview
            id="multilineText1"
            index={6}
          />
        ),
      },
      { element: <HeaderPreview id="header1" index={7} /> },
      { element: <ImagePreview id="image1" index={8} /> },
    ],
  },
];


const FormLayout = () => {
  const { formId, formDetails } = useFormContext(); // Pull formId + metadata
  const { organizationId, currentUser } = useAuth() || {};

  const {
    layoutConfig,
    setLayoutConfig,
    fieldsMetadata,
    showDropZones,
    setShowDropZones,
    handleDrop,
    handleDragStart,
    handleDragEnd,
    selectedField,
    selectField,
    saveLayout,
    publishLayout,
    saveError,
    isLoading: layoutLoading,
    setIsLoading,
  } = useFormLayout(); // Use context states and methods

  const { selectedFieldForEdit } = useFieldEdit(); // Use FieldEditContext
  const {
    addFieldMetadata,
    isLoading: metadataLoading,
  } = useFieldMetadata(); // Use FieldMetadataContext

  // Combined state to track overall loading
  const [isOverallLoading, setIsOverallLoading] = useState(true);

  // Org-managed field palette state
  // - populated centrally from the organization registry
  // - consumed only by the builder sidebar
  const [managedFields, setManagedFields] = useState([]);
  const [managedFieldsLoading, setManagedFieldsLoading] = useState(false);

  // ────────────────────────────────────────────────────────────────
  // Asset Upload Form detection:
  //   - When true, we expose the "Asset management" palette group
  //     (starting with Asset Type; later we can add Tags, Status, etc.)
  // ────────────────────────────────────────────────────────────────
  const isAssetUploadForm =
    formDetails?.isAssetUploadForm === true ||
    formDetails?.formType === 'assetUpload';

  const fetchManagedFields = useCallback(async () => {
    if (!organizationId) {
      setManagedFields([]);
      return;
    }

    setManagedFieldsLoading(true);

    try {
      const response = await listManagedFieldsForOrganization({
        organizationId,
        includeArchived: false,
      });

      if (response?.status === 'success') {
        const activeFields = Array.isArray(response.data)
          ? response.data.filter((field) => field?.status !== 'archived')
          : [];

        setManagedFields(activeFields);

        console.log('[FormLayout] Loaded managed fields for organization:', {
          organizationId,
          count: activeFields.length,
          managedFieldIds: activeFields.map((field) => field.id),
        });
      } else {
        console.warn('[FormLayout] Failed to load managed fields:', response);
        setManagedFields([]);
      }
    } catch (error) {
      console.error('[FormLayout] Error loading managed fields for organization:', error);
      setManagedFields([]);
    } finally {
      setManagedFieldsLoading(false);
    }
  }, [organizationId]);

  const sections = useMemo(() => {
    const organizationFieldSection =
      managedFields.length > 0
        ? [
            {
              title: 'Organization fields',
              items: managedFields.map((managedField, idx) => ({
                element: (
                  <OrganizationManagedFieldPreview
                    key={managedField.id}
                    managedField={managedField}
                    index={200 + idx}
                  />
                ),
              })),
            },
          ]
        : [];

    if (!isAssetUploadForm) {
      return [
        ...BASE_LAYOUT_SECTIONS,
        ...organizationFieldSection,
      ];
    }

    return [
      ...BASE_LAYOUT_SECTIONS,
      ...organizationFieldSection,
      {
        title: 'Asset management',
        items: [
          {
            element: (
              <AssetTypeFieldPreview
                id="assetTypeFieldPreview"
                index={100}
              />
            ),
          },
          {
            element: (
              <VersionNumberFieldPreview
                id="versionNumberFieldPreview"
                index={101}
              />
            ),
          },
          // Later: add Tags field preview, Status, etc.
        ],
      },
    ];
  }, [isAssetUploadForm, managedFields]);


    // Effect to wait for formId from FormContext
    useEffect(() => {
        if (!formId) {
            setIsOverallLoading(true); // Keep loading state active until formId is available
        } else {
            setIsOverallLoading(false); // formId is available
        }
    }, [formId]);

    // Load centrally managed org fields for the sidebar palette.
    // This is builder-only consumption of centrally created field definitions.
    useEffect(() => {
        if (!organizationId) {
            setManagedFields([]);
            return;
        }

        fetchManagedFields();
    }, [organizationId, fetchManagedFields]);

    // Effect to handle selecting a field for edit in the builder canvas
    //
    // IMPORTANT:
    // This effect should only synchronize visual selection between
    // FieldEditContext and FormLayoutContext.
    //
    // It should NOT write selectedFieldForEdit.metadata into FieldMetadataContext.
    // FieldMetadataContext is the committed metadata source. selectedFieldForEdit
    // is an edit-buffer/screen-selection object, and pushing it globally here
    // causes every settings-panel edit to become an immediate metadata commit.
    //
    // HeaderSettings now owns local draft state while typing and commits small
    // patches intentionally. BasicSettings and other settings panels should also
    // commit through their FieldEditContext/saveFieldChanges flow instead of
    // relying on this layout-level side effect.
useEffect(() => {
    if (selectedFieldForEdit && selectedFieldForEdit.id && formId) {
      const existsInLayout = Object.values(layoutConfig || {}).flat()
        .some(f => f.id === selectedFieldForEdit.id);

      if (existsInLayout) {
        // Field still exists, safe to select visually in the builder canvas.
        // Do not call updateFieldMetadata here. Metadata commits belong to the
        // field settings save/commit path, not the layout selection path.
        selectField(selectedFieldForEdit.id);
      } else {
        console.warn(`[FormLayout] Attempted to re-select a deleted field ${selectedFieldForEdit.id}. Skipping.`);
      }
    } else if (selectedFieldForEdit === null) {
      // No field selected—this is fine
    } else {
      console.warn('[FormLayout] Invalid selectedFieldForEdit or fieldId:', selectedFieldForEdit);
    }
}, [selectedFieldForEdit?.id, selectField, formId, layoutConfig]);

    // Effect to handle layoutConfig and set the overall loading state
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!layoutConfig || Object.keys(layoutConfig).length === 0) {
                setIsLoading(true); // LayoutConfig is not initialized
            } else {
                setIsLoading(false); // LayoutConfig is available
                // Ensure metadata for all fields in layoutConfig
                Object.values(layoutConfig).flat().forEach((field) => {
                    if (!field.id) {
                        console.warn("[FormLayout] Found a field with a null or undefined ID:", field);
                    } else if (!fieldsMetadata[field.id]) {
                        // Add metadata for the field
                        addFieldMetadata(field.id, field.type);
                    }
                });
            }
        }, 100);

        return () => clearTimeout(timeout);
    }, [layoutConfig, fieldsMetadata, setIsLoading, addFieldMetadata]);

    // Combine layoutLoading / metadataLoading / managed field loading
    // to set the overall loading state
    useEffect(() => {
        setIsOverallLoading(layoutLoading || metadataLoading || managedFieldsLoading || !formId);
    }, [layoutLoading, metadataLoading, managedFieldsLoading, formId]);

    const handleBack = () => {
        selectField(null); // Reset selectedField to null to go back to the default view
    };

    const handleSave = () => {
        saveLayout(true); // Save as a draft
    };

    const handlePublish = () => {
        publishLayout(); // Publish the layout
    };

 // Show spinner only if something in the canvas depends on metadata that
 // is still loading.  When every real field row is gone, skip the spinner.
 const hasContentRows = Object.keys(layoutConfig || {}).some(
   (k) => k.startsWith('row') && (layoutConfig[k]?.length ?? 0) > 0
 );

 if (!layoutConfig || (isOverallLoading && hasContentRows)) {
   return (
     <Box
       display="flex"
       justifyContent="center"
       alignItems="center"
       height="100vh"
     >
       <CircularProgress />
     </Box>
   );
 }

  return (
    <DndProvider backend={HTML5Backend}>
      <BuilderDragLayer />
            {/* Top-level flex container -- occupies *exactly* the viewport
               minus the two fixed headers:
                   • 48px  FormBuilderNavbarComponent
                   • 48px  FormBuilderToolbar
               = 96px total.  Adjust this number if either header height changes. */}
             <Box
               display="flex"
              height="calc(100vh - 96px)"   /* 🆕  subtract header stack */
               bgcolor="#f0f0f0"
               sx={{ overflow: 'hidden' }}    /* keep document from scrolling */
             >

                <SidePanel
                  sections={sections}
                  selectedField={selectedField}
                  onBack={handleBack}
                  onSave={handleSave}
                  onPublish={handlePublish}
                />

                {/* Builder canvas
                   – height: 100vh  → matches viewport height
                   – overflow-y: auto → its *own* vertical scrollbar
                   (SidePanel keeps its own 100vh + overflow auto)            */}
                <Box
                  flex={1}
                  p={3}
                  /* Fill the parent (already sized to viewport-minus-headers) */
                  sx={{ 
                    overflowY: 'auto', 
                    height: '100%', 
                    minHeight: 0,           // 🆕 allow flex child to shrink and scroll properly
                  }} /* inner scrollbar */
                >


                <FormLayoutUI
                    layoutConfig={layoutConfig}
                    fieldsMetadata={fieldsMetadata}
                    moveItem={handleDrop}
                    mode="builder"
                />
                </Box>
            </Box>
            {saveError && (
                <Snackbar open={Boolean(saveError)} autoHideDuration={6000}>
                    <Alert severity="error" sx={{ width: '100%' }}>
                        {saveError}
                    </Alert>
                </Snackbar>
            )}
        </DndProvider>
    );
};

export default FormLayout;
